import { useState } from 'react';
import {
  getCheckoutReturnToken,
  useBillingSnapshot,
  type BillingSnapshotResponse,
} from './useBillingSnapshot.js';

export { getCheckoutReturnToken } from './useBillingSnapshot.js';

type SubscriptionUser = {
  id: string;
} | null;

interface UseSubscriptionAccessOptions {
  user: SubscriptionUser;
  isBypassSession: boolean;
  forceRefresh?: boolean;
}

interface UseSubscriptionAccessResult {
  isLoading: boolean;
  hasPaidAccess: boolean;
  hasResolved: boolean;
  error: Error | null;
}

type SubscriptionAccessSnapshot = {
  subscription: { status?: string } | null;
  customer?: {
    providerCustomerId?: string | null;
  } | null;
  verification?: {
    state?: 'pending' | 'verified_paid' | 'verified_unpaid' | 'failed';
  } | null;
};

function hasTerminalUnpaidSubscriptionStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }

  return (
    status !== 'active' &&
    status !== 'trialing' &&
    status !== 'past_due' &&
    status !== 'incomplete'
  );
}

export function buildSubscriptionAccessPath(
  checkoutReturnToken: string | null | undefined,
): string {
  return checkoutReturnToken ? '/billing/subscription?refresh=1' : '/billing/subscription';
}

export function shouldForceProtectedBootstrapBillingRefresh(params: {
  userId: string | null;
  isBypassSession: boolean;
  checkoutReturnToken: string | null | undefined;
  hasCachedBillingSnapshot: boolean;
}): boolean {
  const { userId, isBypassSession, checkoutReturnToken } = params;

  if (!userId || isBypassSession) {
    return false;
  }

  if (checkoutReturnToken) {
    return true;
  }

  return false;
}

export function resolveSubscriptionAccessState(
  data: SubscriptionAccessSnapshot | BillingSnapshotResponse | null | undefined,
): Pick<UseSubscriptionAccessResult, 'hasPaidAccess' | 'hasResolved'> {
  const status = data?.subscription?.status?.toLowerCase();
  const hasBillingCustomer =
    typeof data?.customer?.providerCustomerId === 'string' &&
    data.customer.providerCustomerId.trim().length > 0;
  const hasLocalPaidAccess =
    status === 'active' ||
    status === 'trialing' ||
    status === 'past_due' ||
    status === 'incomplete';

  if (hasLocalPaidAccess) {
    return {
      hasPaidAccess: true,
      hasResolved: true,
    };
  }

  const verificationState = data?.verification?.state ?? 'pending';

  if (verificationState === 'verified_paid') {
    return {
      hasPaidAccess: true,
      hasResolved: true,
    };
  }

  if (verificationState === 'verified_unpaid') {
    return {
      hasPaidAccess: false,
      hasResolved: true,
    };
  }

  if (hasTerminalUnpaidSubscriptionStatus(status)) {
    return {
      hasPaidAccess: false,
      hasResolved: true,
    };
  }

  if (!hasLocalPaidAccess && verificationState === 'pending' && !hasBillingCustomer) {
    return {
      hasPaidAccess: false,
      hasResolved: true,
    };
  }

  return {
    hasPaidAccess: false,
    hasResolved: false,
  };
}

export function useSubscriptionAccess({
  user,
  isBypassSession,
  forceRefresh = false,
}: UseSubscriptionAccessOptions): UseSubscriptionAccessResult {
  const userId = user?.id ?? null;
  const [checkoutReturnToken] = useState(() =>
    typeof window === 'undefined' ? null : getCheckoutReturnToken(window.location.search),
  );
  const subscriptionQuery = useBillingSnapshot({
    userId,
    isBypassSession,
    refreshOnReturn: Boolean(checkoutReturnToken) || forceRefresh,
    refetchWhenVerificationPending: true,
  });

  if (!user) {
    return {
      isLoading: false,
      hasPaidAccess: false,
      hasResolved: true,
      error: null,
    };
  }

  if (isBypassSession) {
    return {
      isLoading: false,
      hasPaidAccess: true,
      hasResolved: true,
      error: null,
    };
  }

  if (subscriptionQuery.error) {
    console.error('Failed to check subscription', subscriptionQuery.error);
  }

  const resolvedState = resolveSubscriptionAccessState(subscriptionQuery.data);

  return {
    isLoading: subscriptionQuery.isPending,
    hasPaidAccess: resolvedState.hasPaidAccess,
    hasResolved: resolvedState.hasResolved,
    error: subscriptionQuery.error instanceof Error ? subscriptionQuery.error : null,
  };
}
